import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Check, Clock3, Mail } from 'lucide-react';

import { ApiError } from '@starling/core/api/errors';
import {
  FormError,
  Hint,
  PasswordField,
  PrimaryButton,
  TextField,
} from '@/components/form/primitives';
import { useConfirmPasswordResetMutation, useRequestPasswordResetMutation } from './hooks';
import styles from './LoginPage.module.css';

// Сброс пароля в Vision (доводка приёмки V1 №15) — зеркало Light-страницы
// `auth/PasswordResetPage.tsx` на форм-примитивах Vision (экрана в прототипе
// партнёра нет — согласовано как зеркало Light, lightweight-путь design-workflow).
// Два экрана: «Забыли пароль?» (/forgot-password) и «Новый пароль»
// (/password-reset?token=…). Письмо уходит с брендом/From Vision и ссылкой на
// ЭТОТ домен — `product: 'vision'` в запросе (см. hooks.ts).

function BackToLogin(): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className={styles.switchRow}>
      <Link to="/login" className={styles.switchLink}>
        {t('auth.passwordReset.backToLogin')}
      </Link>
    </div>
  );
}

function StatusBlock({
  icon,
  title,
  text,
}: {
  icon: JSX.Element;
  title: string;
  text: string;
}): JSX.Element {
  return (
    <div className={styles.status}>
      <div className={styles.statusIcon}>{icon}</div>
      <p className={styles.statusTitle}>{title}</p>
      <p className={styles.statusText}>{text}</p>
    </div>
  );
}

/**
 * Экран 1 — «Забыли пароль?». Email → `POST /auth/password-reset/request`
 * (всегда 200, анти-enumeration) → «Проверьте почту» независимо от того,
 * есть ли аккаунт. Не-200 = сеть/инфраструктура → ошибка формы.
 */
export function ForgotPasswordPage(): JSX.Element {
  const { t } = useTranslation();
  const mutation = useRequestPasswordResetMutation();
  const [email, setEmail] = useState('');
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const submit = (): void => {
    const value = email.trim();
    setError(null);
    mutation.mutate(
      { email: value },
      {
        onSuccess: () => setSentTo(value),
        onError: () => setError(t('auth.passwordReset.request.errorNetwork')),
      },
    );
  };

  return (
    <div className={styles.wrap}>
      <div className={styles.card}>
        {sentTo !== null ? (
          <>
            <StatusBlock
              icon={<Mail size={22} aria-hidden="true" />}
              title={t('auth.passwordReset.request.sentTitle')}
              text={t('auth.passwordReset.request.sentText', { email: sentTo })}
            />
            <BackToLogin />
          </>
        ) : (
          <>
            <h1 className={styles.title}>{t('auth.passwordReset.request.title')}</h1>
            <p className={styles.subtitle}>{t('auth.passwordReset.request.subtitle')}</p>
            <form
              onSubmit={(event) => {
                event.preventDefault();
                submit();
              }}
            >
              <FormError>{error}</FormError>
              <TextField
                label={t('auth.email.label')}
                value={email}
                onChange={setEmail}
                type="email"
                inputMode="email"
                name="email"
                autoComplete="email"
                placeholder="you@example.com"
              />
              <PrimaryButton disabled={mutation.isPending || !email.trim()}>
                {t('auth.passwordReset.request.submit')}
              </PrimaryButton>
            </form>
            <BackToLogin />
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Экран 2 — «Новый пароль» (`/password-reset?token=…`). Пароль + повтор →
 * `POST /auth/password-reset/confirm`: 204 → «Пароль изменён» → «Войти»;
 * 400 `reset_token_invalid` → «Ссылка недействительна» + «Запросить новую»;
 * 422 → текст ошибки под формой. Без `token` в URL — сразу «недействительна».
 */
export function PasswordResetPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const mutation = useConfirmPasswordResetMutation();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [phase, setPhase] = useState<'form' | 'done' | 'invalid'>(token ? 'form' : 'invalid');
  const [error, setError] = useState<string | null>(null);

  const submit = (): void => {
    if (!token) {
      setPhase('invalid');
      return;
    }
    setError(null);
    if (password.length < 8) return setError(t('auth.passwordReset.confirm.minLength'));
    if (password !== confirm) return setError(t('auth.passwordReset.confirm.mismatch'));
    mutation.mutate(
      { token, new_password: password },
      {
        onSuccess: () => setPhase('done'),
        onError: (err) => {
          if (err instanceof ApiError) {
            if (err.status === 400 && err.code === 'reset_token_invalid')
              return setPhase('invalid');
            if (err.status === 422)
              return setError(err.message || t('auth.passwordReset.confirm.minLength'));
          }
          setError(t('auth.passwordReset.confirm.errorNetwork'));
        },
      },
    );
  };

  return (
    <div className={styles.wrap}>
      <div className={styles.card}>
        {phase === 'done' && (
          <>
            <StatusBlock
              icon={<Check size={22} aria-hidden="true" />}
              title={t('auth.passwordReset.confirm.doneTitle')}
              text={t('auth.passwordReset.confirm.doneText')}
            />
            <PrimaryButton type="button" onClick={() => navigate('/login', { replace: true })}>
              {t('auth.passwordReset.confirm.doneSubmit')}
            </PrimaryButton>
          </>
        )}
        {phase === 'invalid' && (
          <>
            <StatusBlock
              icon={<Clock3 size={22} aria-hidden="true" />}
              title={t('auth.passwordReset.confirm.invalidTitle')}
              text={t('auth.passwordReset.confirm.invalidText')}
            />
            <PrimaryButton
              type="button"
              onClick={() => navigate('/forgot-password', { replace: true })}
            >
              {t('auth.passwordReset.confirm.invalidSubmit')}
            </PrimaryButton>
            <BackToLogin />
          </>
        )}
        {phase === 'form' && (
          <>
            <h1 className={styles.title}>{t('auth.passwordReset.confirm.title')}</h1>
            <p className={styles.subtitle}>{t('auth.passwordReset.confirm.subtitle')}</p>
            <form
              onSubmit={(event) => {
                event.preventDefault();
                submit();
              }}
            >
              <FormError>{error}</FormError>
              <PasswordField
                label={t('auth.passwordReset.confirm.password')}
                value={password}
                onChange={setPassword}
                name="new-password"
                autoComplete="new-password"
              />
              <Hint>{t('auth.emailSignup.passwordHint')}</Hint>
              <PasswordField
                label={t('auth.passwordReset.confirm.passwordConfirm')}
                value={confirm}
                onChange={setConfirm}
                name="confirm-password"
                autoComplete="new-password"
              />
              <PrimaryButton disabled={mutation.isPending || !password || !confirm}>
                {t('auth.passwordReset.confirm.submit')}
              </PrimaryButton>
            </form>
            <BackToLogin />
          </>
        )}
      </div>
    </div>
  );
}
